// @ts-nocheck
import { types, flow, getRoot, Instance, SnapshotOut } from 'mobx-state-tree';
import { getBaseStore } from './BaseStore';
import { getEnvironment } from './utils';
import { Page } from '../common/enums';
import { RootStore } from './RootStore';
import { PharmacyNeighbor } from './models/PharmacyNeighbor';
import { PharmacyDetails } from './models/PharmacyDetails';
import { PharmacyViews } from './PharmacyStore';
import { PHARMACY_STATUS_AC } from '../common/constants/pharmacyStatusConstants';

export const CalendarChange = types.model({
  newBlackPoints: types.array(types.string),
  newBlackPointsEconomic: types.array(types.string),
  newBlackPointsHealth: types.array(types.string),
  newDayDuties: types.array(types.string),
  newDayNightDuties: types.array(types.string),
  deletedDutyOffRequests: types.array(types.string),
  deletedBlackPoints: types.array(types.string),
  deletedBlackPointsEconomic: types.array(types.string),
  deletedBlackPointsHealth: types.array(types.string),
  deletedDayDuties: types.array(types.string),
  deletedDayNightDuties: types.array(types.string),
  deletedNightDuties: types.array(types.string),
  comment: types.string,
  pharmacyId: types.number,
});

export const PharmacyDetailedStore = types
  .compose(
    getBaseStore<Instance<typeof PharmacyDetails>, SnapshotOut<typeof PharmacyDetails>>(
      PharmacyDetails,
      'pharmacyId',
      'pharmacy',
      { view: 'DETAILED' },
    ),
    types.model('PharmacyDetailedStore', {}),
  )
  .views((self) => ({
    getByIdOrNew(id: number) {
      if (id > 0) {
        return self.datas.get(id.toString());
      } else {
        return {
          pharmacyId: undefined,
          isContributing: true,
          receivesSms: true,
        };
      }
    },
  }))
  .actions((self) => {
    const env = getEnvironment(self);
    const apiFetcher = env.api;
    const root: Instance<typeof RootStore> = getRoot(self);

    const savePharmacyDetails = flow(function*(pharmacyDetails: Instance<typeof PharmacyDetails>) {
      const res = yield self.saveElement(pharmacyDetails);
      if (pharmacyDetails.pharmacyId !== res.pharmacyId) {
        root.ApplicationStore.navigate(`${Page.Pharmacies}/${res.pharmacyId}`);
      }
    });

    const registerMembers = flow(function*(members: Array<Instance<typeof PharmacyDetails>>, group: number) {
      const res = yield env.api.put('pharmacy/registerGroup/' + group, members);
    });

    const unregisterMembers = flow(function*(members: Array<Instance<typeof PharmacyDetails>>) {
      const res = yield env.api.put('pharmacy/unregisterGroup', members);
    });

    const updatePharmacyGeoData = flow(function*(pharmacyDetails: number) {
      try {
        const res = yield env.api.postUnhandled('pharmacy/refreshGeoData/' + pharmacyDetails, null);
        return true;
      } catch (error) {
        return false;
      }
    });

    const refreshWGS64 = flow(function*(pharmacyId: number) {
      try {
        const res = yield env.api.postUnhandled('pharmacy/refreshWGS64Coordinates/' + pharmacyId, null);
        return true;
      } catch (error) {
        return false;
      }
    });

    const loadNeighboringPharmaciesForExchanges = flow(function*(pharmacyId: number, isPharmacy: boolean) {
      const callResult = yield self.loadElement(pharmacyId);
      const array = [...callResult.pharmacyNeighbors];

      if (isPharmacy) {
        const resultSortedPh = array.sort((a, b) => a.distance - b.distance).filter((it) => it.exchangeAllowed);
        const tempResultPh = resultSortedPh.map((item: Instance<typeof PharmacyNeighbor>) => {
          var pharm = root.PharmacyStore.getById(item.neighborId);
          pharm.distance = item.distance;
          return pharm;
        });
        const limitedResult = tempResultPh.filter((ph) => ph.status && ph.status.code === PHARMACY_STATUS_AC && ph.level > 0);

        return limitedResult;
      } else {
        const resultSorted = array.sort((a, b) => a.distance - b.distance);
        const tempResult = resultSorted.map((item: Instance<typeof PharmacyNeighbor>) => {
          var pharm = root.PharmacyStore.getById(item.neighborId);
          pharm.distance = item.distance;
          return pharm;
        });
        const finalResult = tempResult.filter((ph) => ph.status && ph.status.code === PHARMACY_STATUS_AC);
        return finalResult;
      }
    });

    const loadNeighboringPharmacies = flow(function*(pharmacyId: number) {
      const callResult = yield self.loadElement(pharmacyId);
      const array = [...callResult.pharmacyNeighbors];
      const resultSorted = array.sort((a, b) => a.distance - b.distance);
      const finalResult = resultSorted.map((item: Instance<typeof PharmacyNeighbor>) => {
        var pharm = root.PharmacyStore.getById(item.neighborId);
        pharm.distance = item.distance;
        return pharm;
      });
      return finalResult;
    });

    const getAvailablePharmacyList = flow(function*(date: string, apbNumber: number, showAlsoCurrentPharmacy: boolean) {
      const result = yield apiFetcher.get(
        'pharmacy/GetAvailablePharmacyList/' + date + '/' + apbNumber.toString() + '/' + showAlsoCurrentPharmacy,
        {},
      );
      console.log(result);
      return result;
    });

    const getMissingHolidays = flow(function*(criteria) {
      console.log(criteria);
      const result = yield apiFetcher.get('blackpoint/checkMissingHolidays', {
        params: criteria,
      });
      return result;
    });

    const saveCalendarChange = flow(function*(calendarChange: Instance<typeof CalendarChange>) {
      yield env.api.post('pharmacy/calendarChanges', calendarChange);
    });

    const getByIdWithDetails = flow(function*(pharmacyId: number) {
      const result = yield env.api.get(`pharmacy/${pharmacyId}`, {
        params: {
          pharmacyId: pharmacyId,
          view: PharmacyViews.DETAILED,
        },
      });

      self.putInStore([result!]);
      return self.dataList.filter((d) => d.pharmacyId === pharmacyId);
    });

    const togglePairingAllowed = flow(function*(pharmacyId: number, neighborId: number) {
      const result = yield env.api.patch(`pharmacyNeighbors/togglePairingAllowed/${pharmacyId}/${neighborId}`);
      yield getByIdWithDetails(result.pharmacyId);
      yield getByIdWithDetails(result.neighborId);
    });

    const toggleExchangeAllowed = flow(function*(pharmacyId: number, neighborId: number) {
      const result = yield env.api.patch(`pharmacyNeighbors/toggleExchangeAllowed/${pharmacyId}/${neighborId}`);
      yield getByIdWithDetails(result.pharmacyId);
      yield getByIdWithDetails(result.neighborId);
    });

    return {
      savePharmacyDetails,
      updatePharmacyGeoData,
      refreshWGS64,
      loadNeighboringPharmacies,
      loadNeighboringPharmaciesForExchanges,
      getMissingHolidays,
      saveCalendarChange,
      registerMembers,
      unregisterMembers,
      getByIdWithDetails,
      togglePairingAllowed,
      toggleExchangeAllowed,
      getAvailablePharmacyList,
    };
  });
